<template>
  <div
    v-if="activeFilterItems && activeFilterItems.length > 0"
    class="my-0 flex w-full flex-wrap justify-center gap-2 sm:mb-4 sm:mt-2 md:mb-2 md:justify-start"
  >
    <button
      v-for="(filterItem, idx) in activeFilterItems"
      :key="idx"
      class="flex max-h-7 min-h-7 flex-nowrap items-center justify-center gap-2 whitespace-nowrap border border-lightGold-60 bg-lightGold-40 px-2 py-1 text-sm uppercase"
      type="button"
      @click.prevent="removeFilterItem(filterItem)"
    >
      <IconCross class="size-2.5" /> {{ translateLabel(filterItem.name) }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import { toCurrency } from '../../utils/price'
import type { OdoscopeFilter } from '@goldner-group/storefront-odoscope/types'

const props = defineProps<{
  filters: OdoscopeFilter[]
  activeFilters: Record<string, any>
}>()

const emit = defineEmits<{
  (e: 'applyAttributeFilter', slug: string, values: string[]): void
  (e: 'resetFilter', slug: string): void
  (e: 'resetPriceFilter'): void
}>()

const i18n = useI18n()
const { filters, activeFilters } = toRefs(props)

const translateLabel = (label: string) => {
  const translationKey = `filter.${label.toLowerCase()}`
  const translatedText = i18n.t(translationKey)

  return translatedText === translationKey ? label : translatedText
}

const activeFilterItems = computed(() => {
  return getActiveFilters(filters.value, activeFilters.value)
})

const removeFilterItem = (filterItem: any) => {
  if (filterItem.slug === 'minPrice') {
    emit('resetPriceFilter')
    return
  }

  const changedFilter = filters.value.find(
    (f: any) => f.slug === filterItem.slug,
  )

  if (!changedFilter) {
    return
  }

  const filter = activeFilters.value[filterItem.slug]

  if (!Array.isArray(filter)) {
    emit('resetFilter', filterItem.slug)
    return
  }

  // For Odoscope, we work with string values instead of IDs
  emit(
    'applyAttributeFilter',
    filterItem.slug,
    filter.filter((f: any) => f !== filterItem.value),
  )
}

function getActiveFilters(filters: any, activeFilters: any) {
  if (isEmpty(activeFilters)) {
    return []
  }
  return Object.entries(activeFilters)
    .map(([key, value]: any[]) => {
      if (key === 'minPrice') {
        return {
          id: 0,
          name: `${toCurrency(activeFilters.minPrice)} - ${toCurrency(
            activeFilters.maxPrice,
          )}`,
          slug: key as string,
          value: value as string,
        }
      }

      const filter = filters?.find((f: any) => f.slug === key)
      if (!filter) {
        console.warn(`Filter not found: "${key}"`)
        return null
      }

      if (filter.type === 'boolean') {
        return {
          id: filter.id as number,
          name: filter.name as string,
          slug: key as string,
          value: value as boolean,
        }
      }

      // For Odoscope, work with string values instead of numeric IDs
      const targetValues = Array.isArray(value) ? value : [value]
      const values = filter.values.filter((f: any) =>
        targetValues.includes(f.value),
      )

      return values.map((f: any) => ({
        id: f.value as string, // Use value as ID for Odoscope
        name: (f.name.includes('|') ? f.name.split('|')[1] : f.name) as string,
        slug: key as string,
        value: f.value as string,
      }))
    })
    .flat()
    .filter(Boolean) as Array<{
    id: string
    name: string
    slug: string
    value: string | boolean
  }>
}
</script>
